import {
  type AEvent,
  createTrackingEmitterHooks,
  type EventTransformerHooks,
} from "@b2bportal/core-tracking";
import type { FiatPrice, Lodging, RoomProduct } from "@b2bportal/lodging-api";
import type { LodgingCancelScenarioEnum } from "@b2bportal/lodging-cancel-api/lib/api";

export type HotelsRawEvent =
  | HotelAppLoadedRawEvent
  | HotelSearchLoadedRawEvent
  | HotelAvailabilityPageLoadedRawEvent
  | HotelShopPageRoomsLoadedRawEvent
  | HotelShopPageLoadedRawEvent
  | HotelAvailabilityPageMapLoadedRawEvent
  | HotelAvailabilityPageListViewedRawEvent
  | HotelShopPageRoomSelectedRawEvent
  | HotelCancelReservationSuccessViewedRawEvent
  | HotelGuestsSelectionOpenedRawEvent
  | HotelGuestsSelectedRawEvent
  | HotelRoomsSelectedRawEvent
  | HotelCalendarOpenedRawEvent
  | HotelCalendarDateChangedRawEvent
  | HotelCalendarDatesSelectedRawEvent
  | HotelPriceChangedCheckoutModalOpenedRawEvent
  | HotelCancelReservationComponentViewedRawEvent
  | HotelViewedLodgingInListRawEvent
  | HotelShopPageRoomsDetailsOpenedRawEvent
  | HotelViewedRoomInListRawEvent
  | HotelFilterOpenedRawEvent
  | HotelMapCardTappedRawEvent
  | HotelConfirmCancelReservationButtonClickedRawEvent
  | HotelTappedRedoSearchInMapRawEvent
  | HotelSwipedCarouselRawEvent
  | HotelUpdatedListPageRawEvent;

export enum HotelsRawEventName {
  HotelAppLoaded = "HotelAppLoaded",
  HotelAvailabilityPageLoaded = "HotelAvailabilityPageLoaded",
  HotelAvailabilityResultViewed = "HotelAvailabilityResultViewed",
  HotelShopPageRoomsLoaded = "HotelShopPageRoomsLoaded",
  HotelShopPageLoaded = "HotelShopPageLoaded",
  HotelAvailabilityPageMapViewed = "HotelAvailabilityPageMapViewed",
  HotelAvailabilityPageListViewed = "HotelAvailabilityPageListViewed",
  HotelShopPageRoomSelected = "HotelShopPageRoomSelected",
  HotelCancelReservationSuccessViewed = "HotelCancelReservationSuccessViewed",
  HotelCancelReservationComponentViewed = "HotelCancelReservationComponentViewed",
  HotelFilterOpened = "HotelFilterOpened",
  HotelConfirmCancelReservationButtonClicked = "HotelConfirmCancelReservationButtonClicked",
  HotelSearchLoaded = "HotelSearchLoaded",
  HotelShopPageRoomsDetailsOpened = "HotelShopPageRoomsDetailsOpened",
  HotelGuestsSelectionOpened = "HotelGuestsSelectionOpened",
  HotelGuestsSelected = "HotelGuestsSelected",
  HotelRoomsSelected = "HotelRoomsSelected",
  HotelCalendarOpened = "HotelCalendarOpened",
  HotelCalendarDateChanged = "HotelCalendarDateChanged",
  HotelCalendarDatesSelected = "HotelCalendarDatesSelected",
  HotelPriceChangedCheckoutModalOpened = "HotelPriceChangedCheckoutModalOpened",
  HotelMapCardTapped = "HotelMapCardTapped",
  HotelViewedLodgingInList = "HotelViewedLodgingInList",
  HotelSwipedCarousel = "HotelSwipedCarousel",
  HotelViewedRoomInList = "HotelViewedRoomInList",
  HotelTappedRedoSearchInMap = "HotelTappedRedoSearchInMap",
  HotelUpdatedListPage = "HotelUpdatedListPage",
}

export type HotelSearchLoadedRawEvent = {
  name: HotelsRawEventName.HotelSearchLoaded;
};

export type HotelAppLoadedRawEvent = {
  name: HotelsRawEventName.HotelAppLoaded;
  properties: {
    entry: string;
  };
};

export type HotelAvailabilityPageLoadedRawEvent = {
  name: HotelsRawEventName.HotelAvailabilityPageLoaded;
  properties?: {
    pageIndex?: number;
    pagePropertyCount?: number;
  };
};
export type HotelShopPageLoadedRawEvent = {
  name: HotelsRawEventName.HotelShopPageLoaded;
};
export type HotelShopPageRoomsLoadedRawEvent = {
  name: HotelsRawEventName.HotelShopPageRoomsLoaded;
};
export type HotelShopPageRoomsDetailsOpenedRawEvent = {
  name: HotelsRawEventName.HotelShopPageRoomsDetailsOpened;
};
export type HotelAvailabilityPageMapLoadedRawEvent = {
  name: HotelsRawEventName.HotelAvailabilityPageMapViewed;
};
export type HotelAvailabilityPageListViewedRawEvent = {
  name: HotelsRawEventName.HotelAvailabilityPageListViewed;
};
export type HotelViewedLodgingInListRawEvent = {
  name: HotelsRawEventName.HotelViewedLodgingInList;
  properties: {
    hotelId: string;
  };
};

export type HotelSwipedCarouselRawEvent = {
  name: HotelsRawEventName.HotelSwipedCarousel;
  properties: {
    hotelId: string;
  };
};

export type HotelUpdatedListPageRawEvent = {
  name: HotelsRawEventName.HotelUpdatedListPage;
  properties?: {
    pageIndex?: number;
    pagePropertyCount?: number;
  };
};

export type HotelShopPageRoomSelectedRawEvent = {
  name: HotelsRawEventName.HotelShopPageRoomSelected;
  properties: {
    rate: RoomProduct;
  };
};
export type HotelViewedRoomInListRawEvent = {
  name: HotelsRawEventName.HotelViewedRoomInList;
  properties: {
    room_id: string;
    room_index_in_shop: number;
    room_media_count: number;
    selectable_rates_count: number;
  };
};

export type HotelCancelReservationSuccessViewedRawEvent = {
  name: HotelsRawEventName.HotelCancelReservationSuccessViewed;
  properties: {
    cancel_scenario: LodgingCancelScenarioEnum;
  };
};

export type HotelCancelReservationComponentViewedRawEvent = {
  name: HotelsRawEventName.HotelCancelReservationComponentViewed;
  properties: {
    cancel_scenario: LodgingCancelScenarioEnum;
  };
};
export type HotelFilterOpenedRawEvent = {
  name: HotelsRawEventName.HotelFilterOpened;
};

export type HotelConfirmCancelReservationButtonClickedRawEvent = {
  name: HotelsRawEventName.HotelConfirmCancelReservationButtonClicked;
  properties: {
    cancel_scenario: LodgingCancelScenarioEnum;
  };
};

export type HotelGuestsSelectionOpenedRawEvent = {
  name: HotelsRawEventName.HotelGuestsSelectionOpened;
  properties: {
    adults: number;
    childrenAges: number[];
  };
};
export type HotelGuestsSelectedRawEvent = {
  name: HotelsRawEventName.HotelGuestsSelected;
  properties: {
    adults: number;
    childrenAges: number[];
  };
};
export type HotelRoomsSelectedRawEvent = {
  name: HotelsRawEventName.HotelRoomsSelected;
  properties: {
    rooms: number;
  };
};

export type HotelCalendarOpenedRawEvent = {
  name: HotelsRawEventName.HotelCalendarOpened;
};

export type HotelCalendarDateChangedRawEvent = {
  name: HotelsRawEventName.HotelCalendarDateChanged;
  properties: {
    startDate: string;
    endDate?: string;
  };
};
export type HotelCalendarDatesSelectedRawEvent = {
  name: HotelsRawEventName.HotelCalendarDatesSelected;
  properties: {
    startDate: string;
    endDate: string;
  };
};
export type HotelPriceChangedCheckoutModalOpenedRawEvent = {
  name: HotelsRawEventName.HotelPriceChangedCheckoutModalOpened;
  properties: {
    initialPrice: FiatPrice;
    updatedPrice: FiatPrice;
  };
};

export type HotelMapCardTappedRawEvent = {
  name: HotelsRawEventName.HotelMapCardTapped;
  properties: {
    hotelId: string;
  };
};

export type HotelTappedRedoSearchInMapRawEvent = {
  name: HotelsRawEventName.HotelTappedRedoSearchInMap;
};

export const {
  useEmitRawEvent: useEmitCoreHotelsRawEvent,
  useEmitRawEventOnce: useEmitCoreHotelsRawEventOnce,
} = createTrackingEmitterHooks<HotelsRawEvent>();

export type TransformerHooks = EventTransformerHooks<HotelsRawEvent, AEvent>;
